import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Plus,
  Search,
  Filter,
  ArrowLeft,
  Building2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
} from "lucide-react";
import { B2BModuleGate, ModuleLimitBanner } from "@/components/b2b";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/seo/SEO";
import SubcontractorCard from "@/components/subcontractors/SubcontractorCard";
import SubcontractorDialog from "@/components/subcontractors/SubcontractorDialog";
import SubcontractorDetailSheet from "@/components/subcontractors/SubcontractorDetailSheet";
import SubcontractorInviteDialog from "@/components/subcontractors/SubcontractorInviteDialog";

export interface Subcontractor {
  id: string;
  company_name: string;
  siret: string | null;
  siren: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  status: string;
  compliance_score: number;
  last_compliance_check: string | null;
  notes: string | null;
  created_at: string;
  access_token: string | null;
  invitation_sent_at: string | null;
}

const Subcontractors = () => {
  const navigate = useNavigate();
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubcontractor, setEditingSubcontractor] = useState<Subcontractor | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<Subcontractor | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [invitingSubcontractor, setInvitingSubcontractor] = useState<Subcontractor | null>(null);

  useEffect(() => {
    fetchSubcontractors();
  }, []);

  const fetchSubcontractors = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("subcontractors")
        .select("*")
        .eq("user_id", user.id)
        .order("company_name", { ascending: true });

      if (error) throw error;
      setSubcontractors(data || []);
    } catch (error) {
      console.error("Error fetching subcontractors:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les prestataires",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter subcontractors
  const filteredSubcontractors = subcontractors.filter((sub) => {
    const matchesSearch =
      sub.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.siret?.includes(searchQuery) ||
      sub.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "all" || sub.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: subcontractors.length,
    compliant: subcontractors.filter((s) => s.compliance_score >= 80).length,
    partial: subcontractors.filter((s) => s.compliance_score >= 50 && s.compliance_score < 80).length,
    nonCompliant: subcontractors.filter((s) => s.compliance_score < 50).length,
    active: subcontractors.filter((s) => s.status === "active").length,
  };

  const handleAddNew = () => {
    setEditingSubcontractor(null);
    setDialogOpen(true);
  };

  const handleEdit = (sub: Subcontractor) => {
    setEditingSubcontractor(sub);
    setDialogOpen(true);
  };

  const handleViewDetails = (sub: Subcontractor) => {
    setSelectedSubcontractor(sub);
    setDetailSheetOpen(true);
  };

  const handleInvite = (sub: Subcontractor) => {
    setInvitingSubcontractor(sub);
    setInviteDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("subcontractors")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Prestataire supprimé",
        description: "Le prestataire a été retiré de votre liste",
      });
      fetchSubcontractors();
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le prestataire",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <B2BModuleGate module="subcontractors">
      <SEO
        title="Conformité Prestataires | EasyFinder"
        description="Gérez la conformité de vos sous-traitants et prestataires. Suivez leurs documents légaux en temps réel."
      />

      <div className="min-h-screen bg-background md:pl-16">
        <div className="container max-w-6xl mx-auto px-4 py-6 pb-24 md:pb-6">
          {/* Usage Banner */}
          <ModuleLimitBanner module="subcontractors" />
          
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/documents")}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Conformité Prestataires</h1>
              <p className="text-muted-foreground text-sm">
                Suivez les documents de vos sous-traitants
              </p>
            </div>
            <Button onClick={handleAddNew}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-500/20 bg-emerald-500/5">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-emerald-500/10">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats.compliant}</p>
                  <p className="text-xs text-muted-foreground">Conformes</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-500/20 bg-amber-500/5">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-amber-500/10">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.partial}</p>
                  <p className="text-xs text-muted-foreground">Partiels</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-destructive/20 bg-destructive/5">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-full bg-destructive/10">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-destructive">{stats.nonCompliant}</p>
                  <p className="text-xs text-muted-foreground">Non conformes</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un prestataire..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="inactive">Inactifs</SelectItem>
                <SelectItem value="blocked">Bloqués</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Subcontractors List */}
          {filteredSubcontractors.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">
                  {subcontractors.length === 0
                    ? "Aucun prestataire"
                    : "Aucun résultat"}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {subcontractors.length === 0
                    ? "Ajoutez vos premiers prestataires pour suivre leur conformité"
                    : "Essayez de modifier vos filtres de recherche"}
                </p>
                {subcontractors.length === 0 && (
                  <Button onClick={handleAddNew}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un prestataire
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredSubcontractors.map((sub) => (
                <SubcontractorCard
                  key={sub.id}
                  subcontractor={sub}
                  onView={() => handleViewDetails(sub)}
                  onEdit={() => handleEdit(sub)}
                  onDelete={() => handleDelete(sub.id)}
                  onInvite={() => handleInvite(sub)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <SubcontractorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        subcontractor={editingSubcontractor}
        onSuccess={fetchSubcontractors}
      />

      {/* Detail Sheet */}
      <SubcontractorDetailSheet
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        subcontractor={selectedSubcontractor}
        onRefresh={fetchSubcontractors}
      />

      {/* Invite Dialog */}
      {invitingSubcontractor && (
        <SubcontractorInviteDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          subcontractor={invitingSubcontractor}
          onRefresh={fetchSubcontractors}
        />
      )}
    </B2BModuleGate>
  );
};

export default Subcontractors;
